import React, { useState, useEffect } from 'react';
import { questionnaireService } from '../services/questionnaireService';
import { Questionnaire } from '../types';
import { SearchIcon, TrashIcon, RestoreIcon } from '../components/Icons';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

const QuestionnaireTrashPage: React.FC = () => {
  const { showToast } = useToast();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [deleteQuestionnaireId, setDeleteQuestionnaireId] = useState<number | null>(null);

  useEffect(() => {
    fetchDeletedQuestionnaires();
  }, [page, search]);

  const fetchDeletedQuestionnaires = async () => {
    try {
      setLoading(true);
      const data = await questionnaireService.getDeletedQuestionnaires({
        search: search || undefined,
        page,
        size: 10,
      });

      setQuestionnaires(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch deleted questionnaires:', error);
      showToast('获取垃圾箱失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await questionnaireService.restoreQuestionnaire(id);
      fetchDeletedQuestionnaires();
      showToast('问卷恢复成功', 'success');
    } catch (error) {
      console.error('Failed to restore questionnaire:', error);
      showToast('恢复失败，请重试', 'error');
    }
  };

  const handlePermanentDeleteClick = (id: number) => {
    setDeleteQuestionnaireId(id);
    setShowPermanentDeleteModal(true);
  };

  const handlePermanentDeleteConfirm = async () => {
    if (deleteQuestionnaireId === null) return;

    try {
      await questionnaireService.permanentlyDeleteQuestionnaire(deleteQuestionnaireId);
      setShowPermanentDeleteModal(false);
      setDeleteQuestionnaireId(null);
      fetchDeletedQuestionnaires();
      showToast('问卷已永久删除', 'success');
    } catch (error) {
      console.error('Failed to permanently delete questionnaire:', error);
      showToast('删除失败，请重试', 'error');
    }
  };

  const handlePermanentDeleteCancel = () => {
    setShowPermanentDeleteModal(false);
    setDeleteQuestionnaireId(null);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-surface-container-high text-on-surface',
      PUBLISHED: 'bg-success-100 text-success-700',
      ARCHIVED: 'bg-warning-100 text-warning-700',
    };
    return colors[status] || 'bg-surface-container-high text-on-surface';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-headline text-on-surface mb-2">垃圾箱</h1>
        <p className="text-on-surface-variant font-body">已删除的问卷可以恢复或永久删除</p>
      </div>

      {/* Warning Banner */}
      <div className="mb-6 p-4 bg-yellow-50 shadow-soft rounded-2xl">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium font-label text-yellow-800">注意</h3>
            <div className="mt-1 text-sm text-yellow-700 font-body">
              <p>垃圾箱中的问卷可以恢复。永久删除后将无法恢复，请谨慎操作。</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="max-w-md relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-variant" size={20} />
          <input
            type="text"
            placeholder="搜索问卷标题或描述..."
            className="w-full pl-10 pr-4 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-on-surface-variant font-body">加载中...</div>
        </div>
      ) : questionnaires.length === 0 ? (
        <div className="text-center py-16 bg-surface-container-lowest rounded-3xl shadow-soft">
          <TrashIcon className="mx-auto text-on-surface-variant mb-4" size={48} />
          <h3 className="text-lg font-medium font-headline text-on-surface mb-2">垃圾箱为空</h3>
          <p className="text-on-surface-variant font-body">删除的问卷将显示在这里</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {questionnaires.map((questionnaire) => (
              <div
                key={questionnaire.id}
                className="bg-surface-container-lowest p-6 rounded-2xl shadow-soft hover:shadow-soft-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {questionnaire.questionnaireCode && (
                        <span className="px-2 py-1 bg-surface-container-high text-on-surface-variant rounded text-xs font-mono font-medium font-label">
                          {questionnaire.questionnaireCode}
                        </span>
                      )}
                      <h3 className="text-lg font-semibold text-on-surface">{questionnaire.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium font-label ${getStatusColor(questionnaire.status)}`}>
                        {questionnaire.statusDisplayName}
                      </span>
                      {questionnaire.version && (
                        <span className="px-2 py-1 bg-surface-container-high text-on-surface-variant rounded text-xs font-medium font-label">
                          {questionnaire.version}
                        </span>
                      )}
                    </div>
                    {questionnaire.description && (
                      <p className="text-on-surface-variant text-sm line-clamp-2 mb-2 font-body">{questionnaire.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-on-surface-variant font-label">
                      <span>问题数：{questionnaire.questionCount}</span>
                      <span>创建者：{questionnaire.createdByUsername}</span>
                      <span>创建时间：{new Date(questionnaire.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleRestore(questionnaire.id)}
                      className="flex items-center gap-1 px-3 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-xl text-sm font-medium transition-colors"
                    >
                      <RestoreIcon size={16} />
                      恢复
                    </button>
                    <button
                      onClick={() => handlePermanentDeleteClick(questionnaire.id)}
                      className="flex items-center gap-1 px-3 py-2 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-xl text-sm font-medium transition-colors"
                    >
                      <TrashIcon size={16} />
                      永久删除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-on-surface-variant font-label">
                共 {totalElements} 个已删除问卷
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 bg-surface-container-high rounded-xl hover:bg-surface-container-low disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                <span className="px-3 py-1">
                  第 {page + 1} / {totalPages} 页
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 bg-surface-container-high rounded-xl hover:bg-surface-container-low disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={showPermanentDeleteModal}
        title="永久删除问卷"
        message="确定要永久删除这个问卷吗？此操作无法撤销，问卷将被彻底删除。"
        confirmText="永久删除"
        cancelText="取消"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handlePermanentDeleteConfirm}
        onCancel={handlePermanentDeleteCancel}
      />
    </div>
  );
};

export default QuestionnaireTrashPage;
